import { Box, SelectChangeEvent } from '@mui/material';

import { SelectInput } from '@/components/common/select_input/SelectInput';
import { PartnerDashboardDetailsProps } from '@/models/PartnerDashboard';
import { USCountryId } from '@/utils/constants';

import { StateSelect } from './StateSelect';
import { useContext, useMemo } from 'react';
import useStatesAndCounties from '@/hooks/useStatesAndCounties';
import { CountyProps } from '@/models/County';
import { ConfigContext } from '@/Contexts/ConfigContext';

type Props = {
  disabled: boolean;
  showErrors: boolean;
  partnerInfo: PartnerDashboardDetailsProps;
  labelCountry: string;
  labelState: string;
  labelCounty: string;
  patchPartner: (patch: Partial<PartnerDashboardDetailsProps>) => void;
};

export function LocationSelectionSection({
  disabled,
  showErrors,
  partnerInfo,
  labelCountry,
  labelState,
  labelCounty,
  patchPartner,
}: Props) {
  const { getCountiesInState } = useStatesAndCounties();
  const { countrySelectOptions } = useContext(ConfigContext);
  const handleCountryChange = (e: SelectChangeEvent) => {
    let newCountryId: number | null = null;
    let newCountryName: string | null = partnerInfo?.countryName ?? null;
    let newStateId: string | null = partnerInfo?.stateId ?? null;
    let newStateName: string | null = partnerInfo?.stateName ?? null;
    let newCountyId: string | null = partnerInfo?.countyId ?? null;
    let newCountyName: string | null = partnerInfo?.countyName ?? null;

    if (e.target.value != null) {
      newCountryId = parseInt(e.target.value, 10);
      newCountryName =
        countrySelectOptions.find((option) => option.value === e.target.value)?.label ?? null;

      if (newCountryId !== USCountryId) {
        newStateId = null;
        newStateName = null;
        newCountyId = null;
        newCountyName = null;
      }
    }

    patchPartner({
      countryId: newCountryId,
      stateId: newStateId,
      countyId: newCountyId,
      countryName: newCountryName,
      stateName: newStateName,
      countyName: newCountyName,
    });
  };

  const handleCountyChange = (e: SelectChangeEvent) => {
    let newCountyId: string | null = null;
    let newCountyName: string | null = partnerInfo?.countyName ?? null;

    if (e.target.value != null) {
      newCountyId = e.target.value;
      newCountyName =
        countySelectOptions.find((option) => option.value === e.target.value)?.label ?? null;
    }

    patchPartner({
      countyId: newCountyId,
      countyName: newCountyName,
    });
  };

  const countySelectOptions = useMemo(() => {
    if (partnerInfo?.stateId == null) return [];

    return getCountiesInState(partnerInfo.stateId).map((county: CountyProps) => {
      return { label: county.name, value: county.id };
    });
  }, [partnerInfo?.stateId, getCountiesInState]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SelectInput
        disabled={disabled}
        required
        hasError={showErrors && partnerInfo?.countryId == null}
        id='country-select'
        label={labelCountry}
        value={partnerInfo?.countryId != null ? partnerInfo.countryId.toString() : ''}
        selectOptions={countrySelectOptions}
        onChange={handleCountryChange}
      />

      {partnerInfo?.countryId == USCountryId && (
        <StateSelect
          disabled={disabled}
          required
          hasError={showErrors && partnerInfo?.stateId == null}
          label={labelState}
          value={partnerInfo?.stateId != null ? partnerInfo.stateId.toString() : ''}
          partnerInfo={partnerInfo}
          patchPartner={patchPartner}
        />
      )}

      {partnerInfo?.countryId == USCountryId && (
        <SelectInput
          disabled={disabled}
          required
          hasError={showErrors && partnerInfo?.countyId == null}
          id='county-select'
          label={labelCounty}
          value={partnerInfo?.countyId != null ? partnerInfo.countyId.toString() : ''}
          selectOptions={countySelectOptions}
          onChange={handleCountyChange}
        />
      )}
    </Box>
  );
}
